import { useCallback } from 'react';
import {
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CaseStudyItem, CaseStudySection } from '@/types/case-study';
import { useCaseStudy } from '@/hooks/useCaseStudy';

interface UseSectionDragDropProps {
  caseStudy: CaseStudyItem;
  setCaseStudy: React.Dispatch<React.SetStateAction<CaseStudyItem>>;
  isNewCaseStudy: boolean;
}

interface UseSectionDragDropReturn {
  sensors: ReturnType<typeof useSensors>;
  handleDragEnd: (event: DragEndEvent) => Promise<void>;
}

export const useSectionDragDrop = ({
  caseStudy,
  setCaseStudy,
  isNewCaseStudy,
}: UseSectionDragDropProps): UseSectionDragDropReturn => {
  const { reorderSections } = useCaseStudy();

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = caseStudy.sections.findIndex((section) => section.id === active.id);
      const newIndex = caseStudy.sections.findIndex((section) => section.id === over.id);
      
      if (oldIndex !== newIndex) {
        const reorderedSections = arrayMove(caseStudy.sections, oldIndex, newIndex);
        // Update the order property for each section
        const updatedSections = reorderedSections.map((section, index) => ({
          ...section,
          order: index,
        }));
        
        // Update local state immediately
        setCaseStudy(prev => ({
          ...prev,
          sections: updatedSections,
        }));

        // Save to database if not a new case study
        if (!isNewCaseStudy) {
          try {
            await reorderSections.mutateAsync({
              caseStudyId: caseStudy.id,
              sections: updatedSections,
            });
          } catch (error) {
            console.error('Error reordering sections:', error);
          }
        }
      }
    }
  }, [caseStudy.sections, caseStudy.id, isNewCaseStudy, reorderSections, setCaseStudy]);

  return {
    sensors,
    handleDragEnd,
  };
};
